import React, { createContext, useState, useContext, useEffect } from 'react';
import { toast } from 'sonner';

const ServiceSelectionContext = createContext();

export const useServiceSelection = () => {
  const context = useContext(ServiceSelectionContext);
  if (!context) {
    throw new Error('useServiceSelection must be used within a ServiceSelectionProvider');
  }
  return context;
};

export const ServiceSelectionProvider = ({ children }) => {
  const [selectedServices, setSelectedServices] = useState([]);

  const addService = (service) => {
    setSelectedServices(prev => {
      // Check if service already exists
      if (prev.find(s => s.id === service.id)) {
        toast.info('Service already added to booking');
        return prev;
      }
      toast.success(`${service.name} added to booking!`, {
        description: 'View your selections in the booking page'
      });
      return [...prev, service];
    });
  };

  const removeService = (serviceId) => {
    setSelectedServices(prev => {
      const service = prev.find(s => s.id === serviceId);
      if (service) {
        toast.info(`${service.name} removed from booking`);
      }
      return prev.filter(s => s.id !== serviceId);
    });
  };

  const clearServices = () => {
    setSelectedServices([]);
    toast.info('All services cleared');
  };

  const isServiceSelected = (serviceId) => {
    return selectedServices.some(s => s.id === serviceId);
  };

  const getTotalDuration = () => {
    return selectedServices.reduce((total, service) => {
      // Parse duration from service (e.g., "30 min" -> 30)
      const duration = parseInt(service.duration) || 0;
      return total + duration;
    }, 0);
  };

  const getServiceCount = () => selectedServices.length;

  return (
    <ServiceSelectionContext.Provider
      value={{
        selectedServices,
        addService,
        removeService,
        clearServices,
        isServiceSelected,
        getTotalDuration,
        getServiceCount
      }}
    >
      {children}
    </ServiceSelectionContext.Provider>
  );
};